/**
 * Tiny self-contained markdown renderer for our legal pages — no
 * external dependency. Handles:
 *   #..###### headers  •  **bold**  •  [text](url) links
 *   - / * bullet lists  •  paragraphs  •  fenced code (rendered as <pre>)
 *
 * Output is escaped React (no dangerouslySetInnerHTML).
 */
function renderInline(text, keyBase) {
    const out = [];
    let rest = text;
    let i = 0;
    // bold, links, inline code
    while (rest.length) {
        const bMatch = rest.match(/\*\*(.+?)\*\*/);
        const lMatch = rest.match(/\[([^\]]+)\]\(([^)]+)\)/);
        const cMatch = rest.match(/`([^`]+)`/);
        const next = [bMatch, lMatch, cMatch].filter(Boolean).sort((a, b) => a.index - b.index)[0];
        if (!next) { out.push(rest); break; }
        if (next.index > 0) out.push(rest.slice(0, next.index));
        if (next === bMatch) {
            out.push(<strong key={`${keyBase}-b-${i++}`} className="font-semibold text-foreground">{bMatch[1]}</strong>);
            rest = rest.slice(next.index + next[0].length);
        } else if (next === lMatch) {
            out.push(<a key={`${keyBase}-a-${i++}`} href={lMatch[2]} className="text-primary underline hover:opacity-80">{lMatch[1]}</a>);
            rest = rest.slice(next.index + next[0].length);
        } else {
            out.push(<code key={`${keyBase}-c-${i++}`} className="px-1.5 py-0.5 rounded bg-card border border-border text-primary text-[12px] font-mono">{cMatch[1]}</code>);
            rest = rest.slice(next.index + next[0].length);
        }
    }
    return out;
}

export function renderMarkdown(md) {
    if (!md) return null;
    const lines = md.replace(/\r\n/g, "\n").split("\n");
    const nodes = [];
    let i = 0;
    let key = 0;
    while (i < lines.length) {
        const line = lines[i];
        const trimmed = line.trim();
        // Header
        const hMatch = trimmed.match(/^(#{1,6})\s+(.+)$/);
        if (hMatch) {
            const level = hMatch[1].length;
            const Tag = `h${level}`;
            const sizes = ["text-3xl mt-10 mb-4", "text-2xl mt-8 mb-3", "text-xl mt-6 mb-3", "text-lg mt-5 mb-2", "text-base mt-4 mb-2", "text-sm mt-3 mb-1"];
            nodes.push(<Tag key={key++} className={`${sizes[level - 1]} font-semibold text-foreground tracking-tight`}>{renderInline(hMatch[2], key)}</Tag>);
            i++; continue;
        }
        // Horizontal rule
        if (trimmed === "---" || trimmed === "***") {
            nodes.push(<hr key={key++} className="my-6 border-t border-border" />);
            i++; continue;
        }
        // Fenced code
        if (trimmed.startsWith("```")) {
            const buf = [];
            i++;
            while (i < lines.length && !lines[i].trim().startsWith("```")) { buf.push(lines[i]); i++; }
            i++;
            nodes.push(<pre key={key++} className="bg-background border border-border p-3 text-[11px] overflow-x-auto my-3">{buf.join("\n")}</pre>);
            continue;
        }
        // Ordered list block (1. , 2. ...)
        if (/^\d+\.\s+/.test(trimmed)) {
            const items = [];
            while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
                items.push(lines[i].trim().replace(/^\d+\.\s+/, ""));
                i++;
            }
            nodes.push(<ol key={key++} className="list-decimal pl-6 space-y-1 my-3 text-muted-foreground">
                {items.map((it, idx) => <li key={idx}>{renderInline(it, `${key}-${idx}`)}</li>)}
            </ol>);
            continue;
        }
        // List block
        if (/^[-*]\s+/.test(trimmed)) {
            const items = [];
            while (i < lines.length && /^[-*]\s+/.test(lines[i].trim())) {
                items.push(lines[i].trim().replace(/^[-*]\s+/, ""));
                i++;
            }
            nodes.push(<ul key={key++} className="list-disc pl-6 space-y-1 my-3 text-muted-foreground">
                {items.map((it, idx) => <li key={idx}>{renderInline(it, `${key}-${idx}`)}</li>)}
            </ul>);
            continue;
        }
        // Empty line → paragraph break
        if (!trimmed) { i++; continue; }
        // Paragraph: accumulate consecutive non-empty, non-special lines
        const buf = [trimmed];
        i++;
        while (i < lines.length) {
            const t = lines[i].trim();
            if (!t || /^(#{1,6})\s+/.test(t) || /^[-*]\s+/.test(t) || t.startsWith("```")) break;
            buf.push(t);
            i++;
        }
        nodes.push(<p key={key++} className="text-muted-foreground leading-relaxed my-3">{renderInline(buf.join(" "), key)}</p>);
    }
    return nodes;
}
